import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronRight,
  Users,
  UserCheck,
  UserX,
  Building2,
  Link as LinkIcon,
  Trophy,
  Clock,
  AlertCircle,
  Loader2,
  Flame,
  Sparkles,
  ArrowRightLeft,
  HelpCircle,
  ShieldQuestion,
  CalendarClock,
  BellRing,
} from 'lucide-react';
import { AdminGuard } from '../components/guards';
import { useImportStore } from '../store/importStore';
import type { ImportConfirmation } from '../types/import';

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

const StatCard = ({ label, value, icon, color }: StatCardProps) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
    <div className="flex items-center space-x-3">
      <div className={`p-2 rounded-lg ${color}`}>{icon}</div>
      <div>
        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      </div>
    </div>
  </div>
);

const displayName = (p: { firstName: string; lastName: string; crmFirstName: string | null; crmLastName: string | null }) =>
  `${p.crmFirstName ?? p.firstName} ${p.crmLastName ?? p.lastName}`;

const describeIncoming = (confirmation: ImportConfirmation) => {
  const rows = confirmation.payload?.incoming ?? [];
  if (rows.length === 0) return 'an unnamed incoming listing';
  return rows
    .map((row) => `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || 'Unnamed')
    .join(', ');
};

/**
 * One queued slot: the coach the CRM has on file vs. the coach the directory
 * lists, with no email on one side to tell whether they are the same person.
 */
const ConfirmationRow = ({
  confirmation,
  onDecide,
}: {
  confirmation: ImportConfirmation;
  onDecide: (decision: 'CONFIRM' | 'REJECT') => Promise<void>;
}) => {
  const [busy, setBusy] = useState(false);
  const decided = confirmation.status !== 'PENDING';

  const decide = async (decision: 'CONFIRM' | 'REJECT') => {
    setBusy(true);
    try {
      await onDecide(decision);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 border-b border-gray-200 dark:border-gray-700 last:border-0">
      <div className="text-sm">
        <p className="font-medium text-gray-900 dark:text-gray-100">
          {displayName(confirmation.pst.person)} — {confirmation.pst.organization.name}
        </p>
        <p className="text-gray-500 dark:text-gray-400 mt-0.5">
          {confirmation.pst.sport
            ? `${confirmation.pst.sport.gender} ${confirmation.pst.sport.name}`
            : confirmation.pst.role || 'Staff'}
          {' · directory now lists '}
          {describeIncoming(confirmation)}
        </p>
      </div>
      {decided ? (
        <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
          {confirmation.status === 'CONFIRMED' ? 'Marked as departed' : 'Kept on file'}
          {confirmation.decidedBy ? ` by ${confirmation.decidedBy.displayName}` : ''}
        </span>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={() => decide('CONFIRM')}
            disabled={busy}
            className="px-3 py-1.5 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
          >
            They left
          </button>
          <button
            onClick={() => decide('REJECT')}
            disabled={busy}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            Still here
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Import summary page — shows results after import completes
 */
export const ImportSummary = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const {
    currentBatch,
    confirmations,
    isLoading,
    error,
    clearError,
    fetchBatchDetail,
    fetchConfirmations,
    decideConfirmation,
  } = useImportStore();

  useEffect(() => {
    if (batchId) {
      fetchBatchDetail(batchId);
      fetchConfirmations(batchId);
    }
  }, [batchId, fetchBatchDetail, fetchConfirmations]);

  const stats = currentBatch?.stats || {};
  const pendingConfirmations = confirmations.filter((c) => c.status === 'PENDING');

  return (
    <AdminGuard>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/admin/imports" className="hover:text-gray-700 dark:hover:text-gray-300">
              Data Imports
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-gray-700 dark:text-gray-300">Batch</span>
            <ChevronRight className="h-4 w-4" />
            <span className="text-gray-900 dark:text-gray-100 font-medium">Summary</span>
          </nav>

          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Import Summary
            </h1>
            {currentBatch && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {currentBatch.status === 'COMPLETED'
                  ? 'Import completed successfully'
                  : currentBatch.status === 'IMPORTING'
                    ? 'Import in progress...'
                    : `Status: ${currentBatch.status}`}
              </p>
            )}
          </div>

          {/* Error banner */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between">
              <span className="text-red-800 dark:text-red-300">{error}</span>
              <button
                onClick={clearError}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium"
              >
                Dismiss
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500 dark:text-gray-400">Loading summary...</span>
            </div>
          ) : (
            <>
              {/* Stats grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard
                  label="New Persons Created"
                  value={stats.personsCreated || 0}
                  icon={<Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
                  color="bg-blue-50 dark:bg-blue-900/20"
                />
                <StatCard
                  label="Existing Persons Updated"
                  value={stats.personsUpdated || 0}
                  icon={<UserCheck className="h-5 w-5 text-green-600 dark:text-green-400" />}
                  color="bg-green-50 dark:bg-green-900/20"
                />
                <StatCard
                  label="Rows Skipped"
                  value={stats.personsSkipped || 0}
                  icon={<UserX className="h-5 w-5 text-rose-600 dark:text-rose-400" />}
                  color="bg-rose-50 dark:bg-rose-900/20"
                />
                <StatCard
                  label="New Organizations Created"
                  value={stats.schoolsCreated || 0}
                  icon={<Building2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />}
                  color="bg-purple-50 dark:bg-purple-900/20"
                />
                <StatCard
                  label="Schools Matched"
                  value={stats.schoolsLinked || 0}
                  icon={<LinkIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />}
                  color="bg-indigo-50 dark:bg-indigo-900/20"
                />
                <StatCard
                  label="New Sport Associations"
                  value={stats.associationsCreated || 0}
                  icon={<Trophy className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />}
                  color="bg-yellow-50 dark:bg-yellow-900/20"
                />
                <StatCard
                  label="End-Dated Associations"
                  value={stats.associationsStaled || 0}
                  icon={<Clock className="h-5 w-5 text-gray-600 dark:text-gray-400" />}
                  color="bg-gray-100 dark:bg-gray-700"
                />
                <StatCard
                  label="New Coaches at Customer Schools"
                  value={stats.leadsCustomerSchool || 0}
                  icon={<Sparkles className="h-5 w-5 text-orange-600 dark:text-orange-400" />}
                  color="bg-orange-50 dark:bg-orange-900/20"
                />
                <StatCard
                  label="Blast-Experienced Movers"
                  value={stats.leadsBlastExperiencedMover || 0}
                  icon={<Flame className="h-5 w-5 text-red-600 dark:text-red-400" />}
                  color="bg-red-50 dark:bg-red-900/20"
                />
                <StatCard
                  label="School Moves Recorded"
                  value={stats.moversRecorded || 0}
                  icon={<ArrowRightLeft className="h-5 w-5 text-teal-600 dark:text-teal-400" />}
                  color="bg-teal-50 dark:bg-teal-900/20"
                />
                <StatCard
                  label="Heads-Up Tasks Created"
                  value={stats.digestTasksCreated || 0}
                  icon={<BellRing className="h-5 w-5 text-pink-600 dark:text-pink-400" />}
                  color="bg-pink-50 dark:bg-pink-900/20"
                />
                <StatCard
                  label="Needs Confirmation"
                  value={stats.confirmationsCreated || 0}
                  icon={<HelpCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
                  color="bg-amber-50 dark:bg-amber-900/20"
                />
                <StatCard
                  label="Held for Review (No Email to Compare)"
                  value={stats.associationsStaleSkipped || 0}
                  icon={<ShieldQuestion className="h-5 w-5 text-slate-600 dark:text-slate-400" />}
                  color="bg-slate-100 dark:bg-slate-700"
                />
                <StatCard
                  label="Assignments Deferred to Nightly"
                  value={stats.assignmentsDeferredToNightly || 0}
                  icon={<CalendarClock className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />}
                  color="bg-cyan-50 dark:bg-cyan-900/20"
                />
              </div>

              {/* Confirmation queue */}
              {confirmations.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Needs confirmation ({pendingConfirmations.length})
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-3">
                    The directory lists someone else in these roles, but there is no email on file to
                    tell whether the coach we have really left. Nothing was changed automatically.
                  </p>
                  {confirmations.map((confirmation) => (
                    <ConfirmationRow
                      key={confirmation.id}
                      confirmation={confirmation}
                      onDecide={(decision) =>
                        decideConfirmation(batchId!, confirmation.id, decision)
                      }
                    />
                  ))}
                </div>
              )}

              {/* Errors section */}
              {(stats.errors || 0) > 0 && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-red-800 dark:text-red-300">
                      {stats.errors} error{stats.errors !== 1 ? 's' : ''} during import
                    </p>
                    <p className="text-sm text-red-600 dark:text-red-400 mt-0.5">
                      Some records may not have been imported. Check server logs for details.
                    </p>
                  </div>
                </div>
              )}

              {/* Action links */}
              <div className="flex items-center space-x-4 pt-4">
                <Link
                  to="/people"
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  View Imported People
                </Link>
                <Link
                  to="/admin/imports"
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Back to Imports
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminGuard>
  );
};
